import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./Login-ForgotPassword-ResetPassword.css";
import { login, resendVerificationEmail } from "../api/auth";
import { useAuthUser } from "../components/dashboard/hooks/useAuthUser";

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect");
  const { refreshAuth } = useAuthUser();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showResendHint, setShowResendHint] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setShowResendHint(false);

    try {
      const data = await login(formData);
      localStorage.setItem("token", data.token);
      refreshAuth();
      navigate(redirectTo ? `/${redirectTo}` : "/profile");
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { status?: number; data?: { message?: string; code?: string } };
      };
      setError(axiosErr.response?.data?.message || "Login failed");
      if (
        axiosErr.response?.status === 403 &&
        axiosErr.response?.data?.code === "EMAIL_NOT_VERIFIED"
      ) {
        setShowResendHint(true);
      }
    }
  };

  const handleResendVerification = async () => {
    if (isResending) return;
    setError(null);
    setSuccess(null);
    setIsResending(true);
    try {
      const result = await resendVerificationEmail(formData);
      setSuccess(result.message);
      setShowResendHint(false);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Could not send verification email.";
      setError(msg);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className=" login-container">
      <div className="login-wrapper content-container">
        <div className="wrapper-card pink">
          <div className="info-card login-content">
            <h1>Login</h1>

            {error && <div className="alert alert-danger">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}

            {showResendHint && !success && (
              <p className="login-resend-hint">
                You can also click{" "}
                <button
                  type="button"
                  className="no-style-btn"
                  disabled={isResending}
                  onClick={() => void handleResendVerification()}
                >
                  {isResending ? "sending…" : "here"}
                </button>{" "}
                to receive a new verification link in a new email.
              </p>
            )}

            <form onSubmit={handleSubmit} className="login-form">
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleChange}
                required
              />

              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                required
              />

              <button id="login-btn" type="submit" className="btn">
                Login
              </button>
              <button
                type="button"
                className="no-style-btn"
                onClick={() => navigate("/forgot-password")}
              >
                Forgot password?
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
